import React, { useMemo } from "react";
import { Check, FileUp, Landmark, Loader2, Undo2, X, type LucideIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  SepaExportBatchResponseDto,
  ExportBatchStatusEnum,
} from "../../../../../stores/Sepa/sepa.types";
import { useSepaExportBatch } from "../../../../../stores/Sepa/sepa.queries";
import { getSepaErrorMessage } from "./sepaError";
import { SepaTimelineView, TimelineDisplayEvent, TimelineTone } from "./SepaTimelineView";

const STATUS_LABEL: Record<string, string> = {
  [ExportBatchStatusEnum.Pending]: "Ausstehend",
  [ExportBatchStatusEnum.Completed]: "Erstellt",
  [ExportBatchStatusEnum.Failed]: "Fehlgeschlagen",
  [ExportBatchStatusEnum.SentToBank]: "An Bank gesendet",
  [ExportBatchStatusEnum.Accepted]: "Bestätigt",
  [ExportBatchStatusEnum.Rejected]: "Abgelehnt",
  [ExportBatchStatusEnum.Undone]: "Rückgängig",
  [ExportBatchStatusEnum.Reversed]: "Storniert",
};

const STATUS_CLASS: Record<string, string> = {
  [ExportBatchStatusEnum.Pending]: "border-amber-300 bg-amber-50 text-amber-700",
  [ExportBatchStatusEnum.Completed]: "border-sky-300 bg-sky-50 text-sky-700",
  [ExportBatchStatusEnum.Failed]: "border-rose-300 bg-rose-50 text-rose-700",
  [ExportBatchStatusEnum.SentToBank]: "border-indigo-300 bg-indigo-50 text-indigo-700",
  [ExportBatchStatusEnum.Accepted]: "border-emerald-300 bg-emerald-50 text-emerald-700",
  [ExportBatchStatusEnum.Rejected]: "border-rose-300 bg-rose-50 text-rose-700",
  [ExportBatchStatusEnum.Undone]: "border-slate-300 bg-slate-50 text-slate-500",
  [ExportBatchStatusEnum.Reversed]: "border-slate-300 bg-slate-50 text-slate-500",
};

const userName = (user?: { firstName?: string; lastName?: string } | null): string | null => {
  if (!user) return null;
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name || null;
};

const pushEvent = (
  events: TimelineDisplayEvent[],
  id: string,
  date: string | Date | null | undefined,
  label: string,
  tone: TimelineTone,
  Icon: LucideIcon,
  extra?: { actorName?: string | null; reason?: string | null; struck?: boolean },
) => {
  if (!date) return;
  events.push({
    id,
    date: typeof date === "string" ? date : date.toISOString(),
    label,
    tone,
    Icon,
    actorName: extra?.actorName,
    reason: extra?.reason,
    struck: extra?.struck,
  });
};

/** Turns an export batch's lifecycle fields into an ordered set of timeline events. */
const buildBatchTimeline = (batch: SepaExportBatchResponseDto): TimelineDisplayEvent[] => {
  const events: TimelineDisplayEvent[] = [];

  pushEvent(events, "created", batch.createdAt, "Export erstellt", "blue", FileUp, {
    actorName: userName(batch.createdBy),
  });
  pushEvent(events, "sent", batch.sentAt, "An die Bank gesendet", "violet", Landmark, {
    actorName: userName(batch.sentBy),
  });
  if (batch.status === ExportBatchStatusEnum.Accepted) {
    pushEvent(events, "accepted", batch.acknowledgedAt, "Von der Bank bestätigt", "emerald", Check);
  }
  if (batch.status === ExportBatchStatusEnum.Rejected) {
    pushEvent(events, "rejected", batch.acknowledgedAt, "Von der Bank abgelehnt", "red", X, {
      reason: batch.rejectionReason,
    });
  }
  pushEvent(
    events,
    "undone",
    batch.undoneAt as string | null,
    "Export zurückgerollt",
    "orange",
    Undo2,
    {
      actorName: userName(batch.undoneBy),
      reason: batch.undoReason,
    },
  );

  events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return events;
};

interface Props {
  batchId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Read-only lifecycle timeline for one SEPA export. The query is lazy — it only
 * fires while `open` is true, so nothing is fetched until an export is clicked.
 * The header lists the transaction numbers the export contains.
 */
const SepaExportBatchTimelineDialog: React.FC<Props> = ({ batchId, open, onOpenChange }) => {
  const query = useSepaExportBatch(batchId, open);
  const batch = query.data;
  const events = useMemo(() => (batch ? buildBatchTimeline(batch) : []), [batch]);
  const txnNumbers = useMemo(
    () => (batch?.transactions ?? []).map((t) => t.transactionNumber).filter(Boolean),
    [batch],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1400] flex max-h-[85vh] w-[680px] max-w-[95vw] flex-col gap-0 overflow-hidden p-0">
        <div className="bg-gradient-to-b from-[#a5515f] to-[#8a4150] px-5 py-4">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-semibold text-white">
              {batch ? `Export Nr. ${batch.batchNumber}` : "Export-Verlauf"}
            </DialogTitle>
            {batch && (
              <Badge
                variant="outline"
                className={`text-xs font-semibold ${STATUS_CLASS[batch.status] ?? ""}`}
              >
                {STATUS_LABEL[batch.status] ?? batch.status}
              </Badge>
            )}
          </div>
          <DialogDescription className="mt-0.5 text-sm text-white/80">
            Einbezogene Transaktionen
          </DialogDescription>

          {txnNumbers.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {txnNumbers.map((num) => (
                <span
                  key={num}
                  className="rounded-md bg-white/15 px-2 py-0.5 text-xs font-semibold text-white"
                >
                  {num}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col bg-[#fafafab4] px-5 py-4">
          <p className="mb-3 flex-shrink-0 text-xs font-bold uppercase tracking-wide text-slate-500">
            Aktivität
          </p>

          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : query.isError ? (
            <p className="py-4 text-base font-medium text-red-600">
              {getSepaErrorMessage(query.error, "Verlauf konnte nicht geladen werden.")}
            </p>
          ) : events.length === 0 ? (
            <p className="py-4 text-base font-medium text-slate-600">
              Noch keine Aktivität für diesen Export.
            </p>
          ) : (
            <SepaTimelineView events={events} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SepaExportBatchTimelineDialog;
